import { useStoreLocale } from '@ifixit/i18n/hooks';
import { BlueskyLogo } from './social/bluesky-logo';
import { FacebookLogo } from './social/facebook-logo';
import { InstagramLogo } from './social/instagram-logo';
import { MastodonLogo } from './social/mastodon-logo';
import { RepairEULogo } from './social/repair-eu-logo';
import { RepairOrgLogo } from './social/repair-org-logo';
import { ThreadsLogo } from './social/threads-logo';
import { TiktokLogo } from './social/tiktok-logo';
import { TwitterLogo } from './social/twitter-logo';
import { YoutubeLogo } from './social/youtube-logo';
import type { ReactElement } from 'react';
import { FooterLink } from './Shared';
import { isEULocale } from '@ifixit/i18n/helpers';

type IconType = ReactElement;
interface IconMap {
   'default': IconType;
   'eu': IconType;
}

const socialMediaAccounts = {
   tiktok: {
      url: {
         'default': 'https://tiktok.com/@ifixit.com',
         'eu': 'https://www.tiktok.com/@ifixiteurope',
      },
      icon: <TiktokLogo className="size-6" />,
      ariaLabel: 'TikTok',
   },
   facebook: {
      url: {
         'default': 'https://www.facebook.com/iFixit/',
         'eu': 'https://www.facebook.com/iFixitEU',
         'fr': 'https://www.facebook.com/iFixitFR/',
      },
      icon: <FacebookLogo className="size-6" />,
      ariaLabel: 'Facebook',
   },
   twitter: {
      url: {
         'default': 'https://twitter.com/ifixit',
         'eu': 'https://x.com/ifixitde',
         'fr': 'https://x.com/ifixitfr',
      },
      icon: <TwitterLogo className="size-6" />,
      ariaLabel: 'Twitter',
   },
   instagram: {
      url: {
         'default': 'https://www.instagram.com/ifixit/',
         'eu': 'https://www.instagram.com/ifixiteurope/',
         'fr': 'https://www.instagram.com/ifixitfrance/',
      },
      icon: <InstagramLogo className="size-6" />,
      ariaLabel: 'Instagram',
   },
   youtube: {
      url: {
         'default': 'https://www.youtube.com/user/iFixitYourself',
      },
      icon: <YoutubeLogo className="size-6" />,
      ariaLabel: 'YouTube',
   },
   mastodon: {
      url: {
         'default': 'https://mastodon.social/@iFixit',
      },
      icon: <MastodonLogo className="size-6" />,
      ariaLabel: 'Mastodon',
   },
   bluesky: {
      url: {
         'default': 'https://bsky.app/profile/ifixit.com',
         'eu': 'https://bsky.app/profile/ifixiteurope.bsky.social',
      },
      icon: <BlueskyLogo />,
      ariaLabel: 'Bluesky',
   },
   threads: {
      url: {
         'default': 'https://www.threads.com/@ifixit',
         'eu': 'https://www.threads.com/@ifixiteurope',
         'fr': 'https://www.threads.com/@ifixitfrance',
      },
      icon: <ThreadsLogo className="size-6" />,
      ariaLabel: 'Threads',
   },
   repairorg: {
      url: {
         'default': 'https://repair.org',
         'eu': 'https://repair.eu',
      },
      icon: {
         'default': <RepairOrgLogo className="size-6" />,
         'eu': <RepairEULogo className="size-6" />,
      },
      ariaLabel: 'The Repair Association',
   },
};

export function SocialMediaSection() {
   return (
      <div className="flex flex-row flex-wrap items-center justify-center gap-4">
         {Object.entries(socialMediaAccounts).map(([key, value]) => (
            <SocialMediaFooterLink
               key={key}
               account={key as keyof typeof socialMediaAccounts}
               ariaLabel={value.ariaLabel}
               icon={value.icon}
            />
         ))}
      </div>
   );
}

function SocialMediaFooterLink({
   ariaLabel,
   account,
   icon,
}: {
   ariaLabel: string;
   account: keyof typeof socialMediaAccounts;
   icon: IconType | IconMap;
}) {
   const socialUrl = useSocialUrl(account);
   const iconElement = useIcon(account, icon);
   return (
      <FooterLink
         aria-label={ariaLabel}
         href={socialUrl}
         icon={iconElement}
         colorClassName="text-gray-400"
      />
   );
}

/**
 * Get the appropriate social media URL based on the platform key and store locale.
 * For fr-fr locale, try to use a fr url if available, otherwise fall through to eu.
 */
function useSocialUrl(key: keyof typeof socialMediaAccounts): string {
   const storeLocale = useStoreLocale();
   const urlMap = socialMediaAccounts[key].url as Record<string, string>;
   const isEuropean = isEULocale(storeLocale);
   const isFrench = storeLocale === 'fr-fr';

   if (isFrench && urlMap['fr']) {
      return urlMap['fr'];
   }

   if (isEuropean && urlMap['eu']) {
      return urlMap['eu'];
   }

   return urlMap['default'];
}

function useIcon(key: keyof typeof socialMediaAccounts, icon: IconType | IconMap): IconType {
   const storeLocale = useStoreLocale();
   if (key !== 'repairorg') {
      return icon as IconType;
   }

   const iconMap = icon as IconMap;
   return isEULocale(storeLocale) ? iconMap['eu'] : iconMap['default'];
}
